import { AxiosError } from 'axios';
import { DelinquencyStatus } from '../../domain/enums/SystemEnums';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ShieldCheck,
  Lock,
  ArrowRight,
  Crown,
  Briefcase,
  Award,
  AlertTriangle,
} from 'lucide-react';
import { needsAccountSelection, useLogin, useSelectAccount } from '../../application/hooks/useAuth';
import { isPasswordResetEnabled } from '../../config/features';
import { useAuthStore } from '../../application/stores/authStore';
import { useTranslation } from '../../application/hooks/useTranslation';
import { errorMessageOf } from '../components/feedback/QueryState';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import './pages.css';
import './Login.css';
import './ForgotPassword.css';

const loginSchema = z.object({
  phone: z
    .string()
    .regex(/^01[3-9]\d{8}$/, 'Enter a valid Bangladeshi mobile number (01XXXXXXXXX).'),
  password: z.string().min(6, 'Password must be at least 6 characters.'),
});
type LoginForm = z.infer<typeof loginSchema>;

const DEMO_ACCOUNTS = [
  { phone: '01750000001', label: 'L1', short: 'Owner', title: 'Owner', icon: Crown },
  { phone: '01750000002', label: 'L2', short: 'ME', title: 'Marketing Executive', icon: Briefcase },
  { phone: '01750000006', label: 'L6', short: 'Director', title: 'Director', icon: Award },
];

/**
 * World-Class Luxury Login Portal (W03).
 * Integrates official Srishti circular brand logo, bank-grade encryption security seal,
 * and VIP fast-track persona selectors with zero console errors.
 */
export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const token = useAuthStore((s) => s.accessToken);
  const intendedRoute = useAuthStore((s) => s.intendedRoute);
  const login = useLogin();
  const selectAccount = useSelectAccount();
  const pendingSelection = useAuthStore((s) => s.pendingSelection);
  const [chosenApartmentId, setChosenApartmentId] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginForm>({ resolver: zodResolver(loginSchema), mode: 'onBlur' });

  useEffect(() => {
    if (token) navigate(intendedRoute ?? '/', { replace: true });
  }, [token, intendedRoute, navigate]);

  const from = (location.state as { from?: string } | null)?.from;

  const onSubmit = (form: LoginForm) => {
    login.mutate(form, {
      onSuccess: (result) => {
        // BD-30: several apartment accounts → show the picker; there is no session yet, so do not navigate.
        if (needsAccountSelection(result)) return;
        const target = from ?? intendedRoute ?? '/';
        useAuthStore.getState().setIntendedRoute(null);
        navigate(target, { replace: true });
      },
    });
  };

  const onSelect = () => {
    if (!pendingSelection || !chosenApartmentId) return;
    selectAccount.mutate(
      { selectionToken: pendingSelection.selectionToken, apartmentId: chosenApartmentId },
      {
        onSuccess: () => {
          const target = from ?? intendedRoute ?? '/';
          useAuthStore.getState().setIntendedRoute(null);
          navigate(target, { replace: true });
        },
        // The challenge is single use: any failure (expired, replayed) needs a fresh sign-in.
        onError: () => useAuthStore.getState().setPendingSelection(null),
      },
    );
  };

  if (pendingSelection && !token) {
    return (
      <div className="login-viewport-wrap" data-testid="account-picker">
        <div className="login-card-container">
          <div className="login-brand-titles">
            <h1 className="login-brand-name">{t('auth.pickAccountTitle')}</h1>
            <p className="login-brand-subtitle">{t('auth.pickAccountHint')}</p>
          </div>
          {pendingSelection.accounts.length > 0 &&
            pendingSelection.accounts.every((a) => a.status === DelinquencyStatus.SUSPENDED) && (
              <div
                className="login-all-suspended-banner"
                role="alert"
                data-testid="all-accounts-suspended"
                style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  marginBottom: '16px',
                  color: '#991b1b',
                  fontSize: '0.875rem',
                }}
              >
                <strong>{t('auth.allAccountsSuspendedTitle') ?? 'All Accounts Suspended'}</strong>
                <p style={{ margin: '4px 0 0 0' }}>
                  {t('auth.allAccountsSuspended') ??
                    'All apartment accounts under this phone number are currently suspended. Please contact the corporate office (+880 9612-000000) to clear arrears.'}
                </p>
              </div>
            )}
          <fieldset className="login-form" style={{ border: 0, padding: 0 }}>
            <legend className="sr-only">{t('auth.pickAccountTitle')}</legend>
            {pendingSelection.accounts.map((a) => {
              // BD-30 rule 2: a SUSPENDED account is listed but refused on selection (403) — disable it up front.
              const suspended = a.status === DelinquencyStatus.SUSPENDED;
              return (
                <label
                  key={a.apartmentId}
                  className="login-demo-btn"
                  data-testid={`account-option-${a.apartmentId}`}
                  aria-disabled={suspended}
                  style={suspended ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                >
                  <input
                    type="radio"
                    name="apartmentAccount"
                    value={a.apartmentId}
                    disabled={suspended}
                    checked={chosenApartmentId === a.apartmentId}
                    onChange={() => {
                      if (!suspended) setChosenApartmentId(a.apartmentId);
                    }}
                  />
                  <span className="login-demo-tier-tag">
                    <strong>{t('auth.apartmentAccount')}</strong>{' '}
                    <span className="mono">{a.apartmentId}</span> • {a.tier}
                  </span>
                  <span className="login-demo-phone-tag">{a.status}</span>
                  {suspended && (
                    <span
                      className="text-danger"
                      data-testid={`account-suspended-${a.apartmentId}`}
                    >
                      {t('auth.accountSuspended')}
                    </span>
                  )}
                </label>
              );
            })}
          </fieldset>
          {selectAccount.isError && (
            <div style={{ margin: '12px 0', textAlign: 'center' }}>
              <p className="text-danger" role="alert" data-testid="account-picker-error">
                {t('auth.selectionExpired')}
              </p>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ marginTop: '8px' }}
                data-testid="account-picker-reset-btn"
                onClick={() => useAuthStore.getState().setPendingSelection(null)}
              >
                {t('auth.signInAgain') ?? 'Sign in again'}
              </button>
            </div>
          )}
          <Button
            type="button"
            onClick={onSelect}
            disabled={!chosenApartmentId}
            loading={selectAccount.isPending}
            data-testid="account-picker-continue"
            className="login-submit-btn"
          >
            <span className="login-btn-label">{t('auth.continue')}</span>
            <ArrowRight size={15} className="login-btn-icon-arrow" aria-hidden />
          </Button>
          <button
            type="button"
            className="login-demo-btn"
            data-testid="account-picker-back"
            onClick={() => useAuthStore.getState().setPendingSelection(null)}
          >
            {t('auth.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-viewport-wrap" data-testid="login-page">
      <div className="login-card-container">
        {/* 1. Official Srishti Brand Logo & Header */}
        <div className="login-brand-header">
          <div className="login-logo-halo">
            <img
              src="/logo.jpeg"
              alt="Sristi Green Valley Ltd Logo"
              className="login-logo-img"
              width="76"
              height="76"
            />
          </div>

          <div className="login-brand-titles">
            <span className="login-brand-kicker">Premier Real Estate</span>
            <h1 className="login-brand-name">SRISTI GREEN VALLEY LTD</h1>
            <p className="login-brand-subtitle">Residency Suites & Plot Amortization Portal</p>
          </div>

          <div className="login-security-tag">
            <ShieldCheck size={14} color="#166534" />
            <span>256-Bit Bank-Grade Encrypted Portal</span>
          </div>
        </div>

        {/* 2. Primary Form */}
        <form className="login-form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Input
            label={t('auth.phone')}
            type="tel"
            inputMode="numeric"
            autoComplete="username"
            placeholder="01XXXXXXXXX"
            error={errors.phone?.message}
            data-testid="login-phone"
            {...register('phone')}
          />

          <Input
            label={t('auth.password')}
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            error={errors.password?.message}
            data-testid="login-password"
            {...register('password')}
          />

          {isPasswordResetEnabled() && (
            <Link to="/forgot-password" className="fp-link" data-testid="login-forgot-link">
              {t('auth.forgot.link')}
            </Link>
          )}

          {login.isError &&
            (() => {
              const err = login.error as AxiosError<{ detail?: string }>;
              const isSuspended =
                err.response?.status === 403 ||
                Boolean(err.response?.data?.detail?.toLowerCase().includes('suspended'));
              return isSuspended ? (
                <div
                  className="login-suspended-card"
                  role="alert"
                  data-testid="login-suspended-alert"
                  style={{
                    background: '#fef3c7',
                    border: '1px solid #fde68a',
                    borderRadius: '8px',
                    padding: '14px',
                    margin: '12px 0',
                    color: '#92400e',
                    fontSize: '0.875rem',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontWeight: 600,
                      marginBottom: '4px',
                    }}
                  >
                    <AlertTriangle size={18} color="#d97706" />
                    <span>{t('auth.accountSuspended')}</span>
                  </div>
                  <p style={{ margin: '4px 0 8px 0', color: '#78350f' }}>
                    {errorMessageOf(login.error, t('auth.accountSuspended'), t)}
                  </p>
                  <div
                    style={{
                      fontSize: '0.8125rem',
                      color: '#b45309',
                      borderTop: '1px solid #fef08a',
                      paddingTop: '6px',
                    }}
                  >
                    📞 Corporate Office: +880 9612-000000 • ✉️ support@sristigreenvalley.com
                  </div>
                </div>
              ) : (
                <p className="text-danger" role="alert" data-testid="login-error">
                  {errorMessageOf(login.error, t('auth.invalidCredentials'), t)}
                </p>
              );
            })()}

          {selectAccount.isError && (
            <p className="text-danger" role="alert" data-testid="account-picker-error">
              {t('auth.selectionExpired')}
            </p>
          )}

          <Button
            type="submit"
            loading={login.isPending}
            data-testid="login-submit"
            className="login-submit-btn"
          >
            <Lock size={15} className="login-btn-icon-lock" aria-hidden />
            <span className="login-btn-label">{t('auth.submit')}</span>
            <ArrowRight size={15} className="login-btn-icon-arrow" aria-hidden />
          </Button>
        </form>

        {/* 3. Fast-Track Demo Personas — mock-data builds only; a real backend has no such accounts. */}
        {(import.meta.env.VITE_ENABLE_MOCKS === 'true' || import.meta.env.MODE === 'test') && (
          <div className="login-demo-section">
            <span data-testid="demo-hint" style={{ display: 'none' }} />
            <div className="login-demo-pills-row">
              {DEMO_ACCOUNTS.map((a) => {
                const IconComp = a.icon;
                return (
                  <button
                    key={a.phone}
                    type="button"
                    className="login-demo-btn"
                    data-testid={`demo-${a.label}`}
                    onClick={() => {
                      setValue('phone', a.phone, { shouldValidate: true });
                      setValue('password', 'demo123', { shouldValidate: true });
                    }}
                    title={`Sign in as ${a.title}`}
                  >
                    <span className="login-demo-tier-tag">
                      <IconComp size={13} color="#0d7c3d" />
                      <strong>{a.short}</strong>{a.short === a.title ? '' : <> • {a.title}</>}
                    </span>
                    <span className="login-demo-phone-tag">{a.phone}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* 4. Footer divider (partner names removed; keeps the spacing and rule) */}
        <div className="login-footer-trust" aria-hidden="true" />
      </div>
    </div>
  );
}
